import * as React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';

import {
    TextField, Typography, makeStyles,
} from '@material-ui/core';

import {
    KeyboardTimePicker, KeyboardDateTimePicker, KeyboardDatePicker,
} from '@material-ui/pickers';
import { TimeIcon, CalendarIcon } from '../../../../common/icons';
import { validateEmail } from '../../../validationUtils';
import { freetextInputTypes } from '../../../constants';
import { getLocalDate, isDateObject, pad2 } from '../../../dateUtils';
import { TextEditor } from '../../../../common/components';
import HtmlContent from '../../HtmlContent';

const useStyles = makeStyles(theme => ({
    inputResponse: {
        border: theme.youwell.borders.inputBox,
        backgroundColor: theme.youwell.colors.inputBackground,
        padding: theme.spacing(2),
    },
    disabledReadonlyInput: {
        color: theme.palette.text.primary,
    },
    htmlContent: {
        border: theme.youwell.borders.dim,
        padding: theme.spacing(1),
    },
}));

export const getInputType = (inputType) => {
    switch (inputType) {
    case freetextInputTypes.email:
        return 'email';
    case freetextInputTypes.phone:
        return 'tel';
    case freetextInputTypes.number:
        return 'number';
    case freetextInputTypes.date:
        return 'date';
    case freetextInputTypes.dateAndTime:
        return 'datetime-local';
    case freetextInputTypes.time:
        return 'time';
    case freetextInputTypes.default:
    case freetextInputTypes.singleLineText:
    default:
        return 'text';
    }
};

const validators = {
    [freetextInputTypes.email]: validateEmail,
};

const FreetextResponse = ({
    freetextResponse, handleResponseUpdate, freetextConfig, disabled, readOnly, onCompletedValid,
}) => {
    const classes = useStyles();
    const translate = useSelector(state => getTranslate(state.localize));
    const [validationError, setValidationError] = React.useState(null);
    const [freetextStateValue, setFreetextStateValue] = React.useState(null);
    const [showValidation, setShowValidation] = React.useState(false);
    const updateAsyncTimer = React.useRef();

    const freetextType = (freetextConfig && freetextConfig.inputType) || freetextInputTypes.default;

    const validate = (value) => {
        if (validators[freetextType]) {
            const validation = validators[freetextType](value);
            setValidationError(validation.valid ? null : validation.errorTextKey);
            return validation.valid;
        }
        return true;
    };

    const handleChange = (event) => {
        if (readOnly) {
            return;
        }
        const value = event.target.value;
        setFreetextStateValue(value);

        if (showValidation) {
            validate(value);
        }
        clearTimeout(updateAsyncTimer.current);
        updateAsyncTimer.current = setTimeout(() => {
            handleResponseUpdate(value);
        }, 200);
    };

    const handleDateTimeChange = (date) => {
        if (readOnly) {
            return;
        }

        setFreetextStateValue(date);

        if (showValidation) {
            validate(date);
        }
        if (date === null || isDateObject(date)) {
            let valueString;
            if (date === null) {
                valueString = null;
            } else {
                switch (freetextType) {
                case freetextInputTypes.time:
                    valueString = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
                    break;
                case freetextInputTypes.dateAndTime:
                    valueString = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
                    break;
                case freetextInputTypes.date:
                default:
                    valueString = `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
                    break;
                }
            }

            handleResponseUpdate(valueString);
        }
    };

    React.useEffect(() => {
        if (readOnly) {
            // reset text if switching from editing to readonly
            setFreetextStateValue(null);
        }
    }, [readOnly]);

    React.useEffect(() => {
        if (!freetextResponse) {
            // reset text if response is cleared from "outside" of component
            setFreetextStateValue(null);
        }
    }, [freetextResponse]);

    const value = freetextStateValue !== null ? freetextStateValue : freetextResponse; // comparing with null to keep empty ('') value

    const onBlur = () => {
        if (value) {
            setShowValidation(true);
            const valid = validate(value);
            if (valid && onCompletedValid) {
                onCompletedValid();
            }
        }
    };

    const sharedPickerProps = {
        className: classes.inputResponse,
        onChange: handleDateTimeChange,
        fullWidth: true,
        autoOk: true,
        clearable: true,
        disableToolbar: true,
        ampm: false,
        minutesStep: 5,
        disabled,
        clearLabel: translate('clear'),
        cancelLabel: translate('cancel'),
        okLabel: translate('ok'),
        invalidDateMessage: translate('time.invalidFormat'),
    };

    switch (freetextType) {
    case freetextInputTypes.date:
        return (
            <KeyboardDatePicker
                {...sharedPickerProps}
                value={getLocalDate(value, false)}
                keyboardIcon={<CalendarIcon />}
                KeyboardButtonProps={{
                    'aria-label': translate('time.setDate'),
                }}
                placeholder="dd.mm.yyyy"
                format="dd.MM.yyyy"
                disabled={disabled || readOnly}
            />
        );
    case freetextInputTypes.time:
        return (
            <>
                <KeyboardTimePicker
                    {...sharedPickerProps}
                    value={getLocalDate(value, false)}
                    keyboardIcon={<TimeIcon />}
                    KeyboardButtonProps={{
                        'aria-label': translate('time.setTime'),
                    }}
                    placeholder="00:00"
                    format="HH:mm"
                    disabled={disabled || readOnly}
                />
            </>
        );
    case freetextInputTypes.dateAndTime:
        return (
            <>
                <KeyboardDateTimePicker
                    {...sharedPickerProps}
                    value={getLocalDate(value, false)}
                    keyboardIcon={<CalendarIcon />}
                    KeyboardButtonProps={{
                        'aria-label': translate('time.setDateAndTime'),
                    }}
                    placeholder="dd.mm.yyyy hh:mm"
                    format="dd.MM.yyyy HH:mm"
                    disabled={disabled || readOnly}
                />
            </>
        );
    case freetextInputTypes.richText:
        return (
            <>
                {disabled || readOnly ? <HtmlContent html={value || ''} className={classes.htmlContent} /> : (
                    <TextEditor
                    // contentLinkProgramId={programPath.programId}
                    // uploadContentProgramId={programPath.programId}
                        limitedClassic
                        disabled={disabled || readOnly}
                        value={value || ''}
                        onChange={handleChange}
                        className={classes.textEditorContent}
                        helperText={validationError ? translate(validationError) : null}
                        error={!!validationError}
                    />
                )}
            </>
        );
    default:
        return (
            <>
                <TextField
                    placeholder={freetextType === freetextInputTypes.default ? translate('taskContent.writeYourResponseHere') : undefined}
                    multiline={freetextType === freetextInputTypes.default}
                    type={getInputType(freetextType)}
                    minRows={3}
                    maxRows={10}
                    fullWidth
                    disabled={disabled || readOnly}
                    value={value || ''}
                    onChange={handleChange}
                    className={classes.inputResponse}
                    onBlur={onBlur}
                    InputProps={{
                        disableUnderline: true,
                        inputProps: { maxLength: freetextConfig?.maxLength },
                        classes: { disabled: readOnly ? classes.disabledReadonlyInput : null },
                    }}
                    helperText={validationError ? translate(validationError) : null}
                    error={!!validationError}
                />
                {freetextConfig?.maxLength > 0 && (
                    <Typography variant="caption" align="right" display="block">
                        {translate('taskContent.maxLengthXofY', { x: value?.length || 0, y: freetextConfig?.maxLength })}
                    </Typography>
                )}
            </>
        );
    }
};

FreetextResponse.propTypes = {
    handleResponseUpdate: PropTypes.func,
    freetextConfig: PropTypes.object,
    freetextResponse: PropTypes.string,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    onCompletedValid: PropTypes.func,
};

FreetextResponse.defaultProps = {
    freetextResponse: null,
    disabled: false,
    handleResponseUpdate: null,
    freetextConfig: null,
    readOnly: false,
    onCompletedValid: null,
};

export default FreetextResponse;
